import { useState, type ElementType } from 'react';
import { useCopy } from '../../i18n';
import { Monitor, Globe, Megaphone, CalendarDays, Settings, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import analytics from '../../services/analyticsService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';
import type { PersonaRole } from '../../types/database';

interface WelcomeModalProps {
  /** Recebe a chave do modelo escolhido (projectTemplates) para semear o primeiro projeto. */
  onStart: (templateKey: string) => void;
  onClose: () => void;
}


// Só a chave e o ícone ficam aqui; rótulo e descrição vêm do idioma.
const PROJECT_TYPES = [
  { key: 'software', icon: Monitor },
  { key: 'website', icon: Globe },
  { key: 'marketing', icon: Megaphone },
  { key: 'evento', icon: CalendarDays },
  { key: 'implantacao', icon: Settings },
] as const;

// Mesma regra dos PROJECT_TYPES: a chave é estável, o rótulo vem do idioma.
// É o que vai para o GA4 e para profiles.persona_role — gravar o rótulo
// traduzido espalharia a mesma persona por três valores diferentes.
const ROLE_KEYS = ['project_manager', 'pmo', 'product_owner', 'coordinator', 'other'] as const;

/**
 * Tela de boas-vindas do primeiro acesso. Duas perguntas (papel e tipo de projeto).
 * O tipo de projeto mapeia 1:1 para um modelo, que semeia um cronograma já populado,
 * para o usuário nunca cair num Gantt vazio.
 */
export default function WelcomeModal({ onStart, onClose }: WelcomeModalProps) {
  const t = useCopy().onboarding.welcome;
  const { user } = useAuth();
  const [role, setRole] = useState<PersonaRole | null>(null);
  const [type, setType] = useState<string | null>(null);

  const handleStart = () => {
    const templateKey = type || 'software';
    analytics.trackOnboardingWelcome(role || 'nao_informado', templateKey);

    // Estas duas respostas são o ÚNICO dado de ICP que o produto coleta, e até
    // aqui só existiam no GA4 (onde não dá para cruzar com conversão). Grava no
    // perfil para a análise ser um GROUP BY em vez de um join que não existe.
    // Fire-and-forget de propósito: o onboarding não pode travar nem falhar por
    // causa de telemetria. Mesmo padrão da atribuição OAuth (AuthContext.tsx:160).
    if (user) {
      supabase
        .from('profiles')
        .update({
          persona_role: role,
          first_project_type: templateKey,
          persona_captured_at: new Date().toISOString(),
        })
        .eq('id', user.id)
        .then(({ error }) => {
          if (error) console.error('[WelcomeModal] Falha ao gravar persona:', error.message);
        });
    }

    onStart(templateKey);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[640px] p-0 gap-0 max-h-[90vh] flex flex-col" data-testid="welcome-modal">
        <DialogHeader className="px-6 py-5 border-b border-[var(--color-border)]">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-[var(--color-primary)]" />
            {t.title}
          </DialogTitle>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            {t.subtitle}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div>
            <label className="text-sm font-medium text-[var(--color-text)] mb-2 block">{t.roleQuestion}</label>
            <div className="flex flex-wrap gap-2">
              {ROLE_KEYS.map(key => (
                <button
                  key={key}
                  type="button"
                  data-testid={`welcome-role-${key}`}
                  onClick={() => setRole(key)}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-all cursor-pointer ${
                    role === key
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium'
                      : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]'
                  }`}
                >
                  {t.roles[key]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[var(--color-text)] mb-2 block">
              {t.typeQuestion}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PROJECT_TYPES.map(pt => {
                const Icon = pt.icon;
                return (
                  <button
                    key={pt.key}
                    type="button"
                    data-testid={`welcome-type-${pt.key}`}
                    onClick={() => setType(pt.key)}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all cursor-pointer ${
                      type === pt.key
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                        : 'border-[var(--color-border)] hover:border-[var(--color-primary)] hover:bg-[var(--color-bg-secondary)]'
                    }`}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--color-primary)]/10 shrink-0">
                      <Icon className="w-5 h-5 text-[var(--color-primary)]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-[var(--color-text)]">{t.projectTypes[pt.key].label}</p>
                      <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{t.projectTypes[pt.key].description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[var(--color-border)]">
          <Button variant="ghost" onClick={onClose} data-testid="welcome-skip">
            {t.skip}
          </Button>
          <Button onClick={handleStart} disabled={!type} className="gap-2" data-testid="welcome-start">
            <Sparkles className="w-4 h-4" />
            {t.start}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
